import React, { useEffect, useState } from "react";
import { TutorialsService } from "@app/tutorials";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import OperationsService from "@app/operations/operationsService";
import HumanaExportStatuses from "@app/operations/HumanaExportStatuses";
import HumanaJobStatistics from "@app/operations/HumanaJobStatistics";
import JobStatuses from "@app/operations/JobStatuses";
import ProviderSetVersionStatus from "@app/operations/ProviderSetVersionStatus";
import UHCJobStatistics from "@app/operations/UHCJobStatistics";
import JobCompletionRate from "@app/operations/job-completion-rate/JobCompletionRate";
import WeekendRunFailedJobs from "@app/operations/WeekendRunFailedJobs";
import WeekendRunHungJobs from "@app/operations/WeekendRunHungJobs";
import WeekendRunMonitoringProjectStatuses from "@app/operations/WeekendRunMonitoringProjectStatuses";
import WeeklyComparison from "@app/operations/WeeklyComparison";

const operationsService = new OperationsService();
const tutorialService = new TutorialsService();

export const WeekendRunMonitoring: React.FC = () => {
  const [humanaReadMe, setHumanaReadMe] = useState<string>("");
  const [uhcReadMe, setUhcReadMe] = useState<string>("");
  const [qesiTroubleshootingReadMe, setQESITroubleshootingReadMe] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>("humana");
  const [activeSubTab, setActiveSubTab] = useState<string>("readme");
  const [activeJobStatisticsTab, setActiveJobStatisticsTab] = useState<string>("byUser");
  const [clientId, setClientId] = useState<number | null>(null);
  const [clients, setClients] = useState<{ clientId: number, name: string }[]>([]);
  const [users, setUsers] = useState<{ userId: number, userName: string }[]>([]);

  useEffect(() => {
    const fetchClients = async () => {
      const clientData = await operationsService.getAllClients();
      const humanaReadMeUri = await tutorialService.getBlobSasUri("weekend-run-monitoring", "HumanaReadMe", "docx");
      const uhcReadMeUri = await tutorialService.getBlobSasUri("weekend-run-monitoring", "UHCReadMe", "docx");
      const qesiTroubleshootingReadMeUri = await tutorialService.getBlobSasUri("weekend-run-monitoring", "QESITroubleshootingReadMe", "docx");
      setClients(clientData);
      setHumanaReadMe(humanaReadMeUri);
      setUhcReadMe(uhcReadMeUri);
      setQESITroubleshootingReadMe(qesiTroubleshootingReadMeUri);
    };
    fetchClients();
  }, []);

  // set the client id to the selected client as necessary
  useEffect(() => {
    if (clients.length > 0) {
      let activeId = clientId;
      switch (activeTab) {
        case "humana":
          activeId = clients.find((c) => c.name === "Humana")?.clientId || activeId;
          break;
        case "unitedHealthcare":
          activeId = clients.find((c) => c.name === "United HealthCare Services")?.clientId || activeId;
          break;
      }
      setClientId(activeId);
    }
  }, [clients, activeTab]);

  const handleMainTabClick = (tab: string) => {
    setActiveTab(tab);
    setActiveSubTab("readme");
    setActiveJobStatisticsTab("byUser");
  };

  const handleSubTabClick = (subTab: string) => {
    setActiveSubTab(subTab);
    setActiveJobStatisticsTab("byUser");
  };

  const renderPane = (tab: string, label: string) => (
    <li className="nav-item">
      <button
        type="button"
        className={`nav-link ${activeTab === tab ? "active" : ""}`}
        onClick={() => handleMainTabClick(tab)}
      >
        {label}
      </button>
    </li>
  );

  const renderSubPane = (subTab: string, label: string) => (
    <li className="nav-item">
      <button
        type="button"
        className={`nav-link ${activeSubTab === subTab ? "active" : ""}`}
        onClick={() => handleSubTabClick(subTab)}
      >
        {label}
      </button>
    </li>
  );

  const renderJobStatisticsPane = (jobStatisticsTab: string, label: string) => (
    <li className="nav-item">
      <button
        type="button"
        className={`nav-link ${activeJobStatisticsTab === jobStatisticsTab ? "active" : ""}`}
        onClick={() => setActiveJobStatisticsTab(jobStatisticsTab)}
      >
        {label}
      </button>
    </li>
  );

  const renderJobStatisticsTabs = (byUserView: React.ReactNode) => (
    <div>
      <div className="tabs-style-4 mb-3">
        <ul className="nav nav-tabs">
          {renderJobStatisticsPane("byUser", "By User")}
          {renderJobStatisticsPane("completionRate", "Completion Rate")}
        </ul>
      </div>
      {activeJobStatisticsTab === "byUser" && byUserView}
      {activeJobStatisticsTab === "completionRate" && (
        <JobCompletionRate clients={clients} clientId={clientId} setClientId={setClientId} />
      )}
    </div>
  );

  return (
    <Page documentTitle="Weekend Run Monitoring">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Operations} items={["Weekend Run Monitoring"]} />
      <PageInner>
        <PageHeader title="Weekend Run Monitoring" disableHR={true}>
        </PageHeader>
      </PageInner>

      <PageSection className="weekend-run-monitoring">
        <div className="tabs-style-2 mb-3">
          <ul className="nav nav-tabs">
            {renderPane("humana", "Humana")}
            {renderPane("unitedHealthcare", "United HealthCare Services")}
          </ul>
        </div>
        <div className="mx-3">
          {activeTab === "humana" && (
            <div>
              <div className="tabs-style-3 mb-3">
                <ul className="nav nav-tabs">
                  {renderSubPane("readme", "ReadMe")}
                  {renderSubPane("qesitroubleshooting", "QESI Troubleshooting")}
                  {renderSubPane("jobStatistics", "Job Statistics")}
                  {renderSubPane("providerSetVersionStatus", "Provider Set Version Status")}
                  {renderSubPane("weekendRunMonitoringProjectStatuses", "Project Statuses")}
                  {renderSubPane("exportsStatuses", "Exports Statuses")}
                  {renderSubPane("failedJobs", "Failed Jobs")}
                  {renderSubPane("hungJobs", "Hung Jobs")}
                </ul>
              </div>
              {activeSubTab === "readme" && <RoleRestricted allowedRoles={[Roles.OPERATIONS_USER, Roles.OPERATIONS_ADMIN]}><a href={humanaReadMe} download="HumanaReadMe.docx">Download: Humana ReadMe</a></RoleRestricted>}
              {activeSubTab === "qesitroubleshooting" && <RoleRestricted allowedRoles={[Roles.OPERATIONS_USER, Roles.OPERATIONS_ADMIN]}><a href={qesiTroubleshootingReadMe} download="QESITroubleshootingReadMe.docx">Download: QESI Troubleshooting ReadMe</a></RoleRestricted>}
              {activeSubTab === "jobStatistics" && renderJobStatisticsTabs(
                <HumanaJobStatistics clients={clients} users={users} setUsers={setUsers} clientId={clientId} setClientId={setClientId} />
              )}
              {activeSubTab === "providerSetVersionStatus" && (
                <ProviderSetVersionStatus clients={clients} clientId={clientId} setClientId={setClientId} />
              )}
              {activeSubTab === "weekendRunMonitoringProjectStatuses" && <WeekendRunMonitoringProjectStatuses clients={clients} clientId={clientId} setClientId={setClientId} />}
              {activeSubTab === "exportsStatuses" && (
                <HumanaExportStatuses clients={clients} users={users} setUsers={setUsers} clientId={clientId} setClientId={setClientId} />
              )}
              {activeSubTab === "failedJobs" && <WeekendRunFailedJobs clients={clients} clientId={clientId} setClientId={setClientId} />}
              {activeSubTab === "hungJobs" && <WeekendRunHungJobs clients={clients} clientId={clientId} setClientId={setClientId} />}
            </div>
          )}
          {activeTab === "unitedHealthcare" && (
            <div>
              <div className="tabs-style-3 mb-3">
                <ul className="nav nav-tabs">
                  {renderSubPane("readme", "ReadMe")}
                  {renderSubPane("weeklyComparison", "Weekly Comparison")}
                  {renderSubPane("jobStatistics", "Job Statistics")}
                  {renderSubPane("jobStatuses", "Job Statuses")}
                  {renderSubPane("weekendRunMonitoringProjectStatuses", "Project Statuses")}
                  {renderSubPane("failedJobs", "Failed Jobs")}
                  {renderSubPane("hungJobs", "Hung Jobs")}
                </ul>
              </div>
              {activeSubTab === "readme" && <RoleRestricted allowedRoles={[Roles.OPERATIONS_USER, Roles.OPERATIONS_ADMIN]}><a href={uhcReadMe} download="UHCReadMe.docx">Download: UHC ReadMe</a></RoleRestricted>}
              {activeSubTab === "weeklyComparison" && <WeeklyComparison clients={clients} clientId={clientId} setClientId={setClientId} />}
              {activeSubTab === "jobStatistics" && renderJobStatisticsTabs(
                <UHCJobStatistics clients={clients} clientId={clientId} setClientId={setClientId} />
              )}
              {activeSubTab === "jobStatuses" && <JobStatuses clients={clients} clientId={clientId} setClientId={setClientId} />}
              {activeSubTab === "weekendRunMonitoringProjectStatuses" && <WeekendRunMonitoringProjectStatuses clients={clients} clientId={clientId} setClientId={setClientId} />}
              {activeSubTab === "failedJobs" && <WeekendRunFailedJobs clients={clients} clientId={clientId} setClientId={setClientId} />}
              {activeSubTab === "hungJobs" && <WeekendRunHungJobs clients={clients} clientId={clientId} setClientId={setClientId} />}
            </div>
          )}
        </div>
      </PageSection>
    </Page>
  );
};